import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset } from 'firebase/auth';
import { Sparkles, Lock } from 'lucide-react';
import { getFirebaseAuth, isFirebaseClientConfigured } from '../lib/firebaseClient';
import { formatFirebaseAuthError } from '../lib/firebaseAuthErrors';
import { useLocale } from '../contexts/LocaleContext';
import { getAppLocale } from '../content/appLocale';
import LocaleSelector from '../components/LocaleSelector';

const ResetPassword = () => {
  const { locale } = useLocale();
  const copy = getAppLocale(locale);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const firebaseOobCode = searchParams.get('oobCode');
  const firebaseMode = searchParams.get('mode');

  const flow = useMemo(() => {
    if (!isFirebaseClientConfigured()) {
      return 'invalid';
    }
    if (firebaseOobCode && (firebaseMode === 'resetPassword' || !firebaseMode)) {
      return 'firebase';
    }
    return 'invalid';
  }, [firebaseMode, firebaseOobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(copy.auth.passwordMismatch);
      return;
    }

    if (newPassword.length < 8) {
      setError(copy.auth.passwordLength);
      return;
    }

    if (flow !== 'firebase') {
      setError(copy.auth.invalidLink);
      return;
    }

    setLoading(true);
    try {
      const auth = getFirebaseAuth();
      await confirmPasswordReset(auth, firebaseOobCode, newPassword);
      alert(copy.auth.passwordResetSuccess);
      navigate('/login');
    } catch (err) {
      setError(formatFirebaseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  if (flow === 'invalid') {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-light text-[#F5F4FF] mb-4">{copy.auth.invalidResetLink}</h1>
          <p className="text-[#B6B3C9] mb-6">
            {copy.auth.invalidResetHelp}
          </p>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="bg-[#7C5CFF] text-white hover:bg-[#6B4DFF] rounded-full px-8 py-3 transition-all font-medium"
          >
            {copy.auth.requestNewLink}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0D] flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-br from-[#7C5CFF] to-[#A88BFF] p-3 rounded-2xl">
                <Sparkles className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <h1 className="text-4xl font-light tracking-tight text-[#F5F4FF] mb-2">{copy.auth.setNewPassword}</h1>
            <p className="text-[#B6B3C9]">{copy.auth.setNewPasswordSubtitle}</p>
          </div>

          <div className="bg-[#141417] border border-[#2B2B35]/50 rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
            <form onSubmit={handleSubmit}>
              {error && (
                <div data-testid="reset-password-error" className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm mb-4">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#F5F4FF] mb-2">{copy.auth.newPassword}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8F8AA8] w-5 h-5" strokeWidth={1.5} />
                  <input
                    data-testid="new-password-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-[#0B0B0D] border border-[#2B2B35] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20 focus:border-[#7C5CFF] transition-all text-[#F5F4FF] placeholder-[#8F8AA8]"
                    placeholder="Enter new password"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-[#F5F4FF] mb-2">{copy.auth.confirmPassword}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8F8AA8] w-5 h-5" strokeWidth={1.5} />
                  <input
                    data-testid="confirm-password-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-[#0B0B0D] border border-[#2B2B35] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20 focus:border-[#7C5CFF] transition-all text-[#F5F4FF] placeholder-[#8F8AA8]"
                    placeholder="Confirm new password"
                    required
                  />
                </div>
              </div>

              <button
                data-testid="reset-password-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#7C5CFF] text-white hover:bg-[#6B4DFF] rounded-full px-8 py-4 transition-all duration-300 shadow-[0_4px_14px_rgba(124,92,255,0.32)] hover:shadow-[0_6px_20px_rgba(124,92,255,0.45)] font-medium tracking-wide disabled:opacity-50"
              >
                {loading ? copy.auth.resetting : copy.auth.resetPasswordAction}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-cover bg-center relative" style={{
        backgroundImage: `url('${copy.media.authHero}')`
      }}>
        <div className="absolute right-6 top-6 z-20">
          <LocaleSelector />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#050507]/75 to-[#050507]/40"></div>
      </div>
    </div>
  );
};

export default ResetPassword;




